import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { DollarSign, TrendingUp, Briefcase, Users, Building2, Wrench, Download, CreditCard, ExternalLink, Receipt } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import StatCard from "./StatCard";

const DirectJobsReportTab = ({ directJobsData, formatCurrency, formatDate, exportToCSV }) => {
  const { isDark } = useTheme();
  if (!directJobsData) return null;

  const { summary, payments_by_type, jobs } = directJobsData;
  const profitColor = isDark ? "text-[#FFCC00]" : "text-amber-600";
  const profitBg = isDark ? "bg-[#FFCC00]/10" : "bg-amber-100";
  const linkColor = isDark ? "text-[#FFCC00] hover:text-[#FFCC00]/80" : "text-blue-600 hover:text-blue-800";
  const btnOutline = isDark ? "border-white/20 text-foreground hover:bg-theme-hover" : "border-gray-300 text-gray-700 hover:bg-gray-100";

  return (
    <div className="space-y-6">
      {/* Responsive grid: 6 cards wrap as 3 + 3 on lg tablets so labels
          like "Company Parts" / "Net Profit" don't truncate. Single-row
          at xl ≥ 1280 px where there's room for 6 cards. */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        <StatCard title="Total Jobs" value={summary.total_jobs} icon={Briefcase} color="text-blue-500" bgColor="bg-blue-500/10" />
        <StatCard title="Total Revenue" value={formatCurrency(summary.total_revenue)} icon={DollarSign} color="text-green-600" bgColor="bg-green-500/10" />
        <StatCard title="Total Parts" value={formatCurrency(summary.total_parts)} icon={Wrench} color="text-orange-500" bgColor="bg-orange-500/10" />
        <StatCard title="Company Parts" value={formatCurrency(summary.company_parts)} icon={Building2} color="text-purple-500" bgColor="bg-purple-500/10" />
        <StatCard title="Tech Parts" value={formatCurrency(summary.tech_parts)} icon={Users} color="text-cyan-600" bgColor="bg-cyan-500/10" />
        <StatCard title="Net Profit" value={formatCurrency(summary.net_profit)} icon={TrendingUp} color={profitColor} bgColor={profitBg} />
        {(summary.total_tax_charged || 0) > 0 && (
          <>
            <StatCard title="Tax Charged" value={formatCurrency(summary.total_tax_charged)} icon={Receipt} color="text-teal-600" bgColor="bg-teal-500/10" data-testid="direct-tax-charged-card" />
            <StatCard title="Tax Collected" value={formatCurrency(summary.total_tax_collected)} icon={Receipt} color="text-teal-600" bgColor="bg-teal-500/10" data-testid="direct-tax-collected-card" />
          </>
        )}
      </div>

      {payments_by_type && payments_by_type.length > 0 && (
        <Card className="glass-card border-theme">
          <CardHeader className="pb-3">
            <CardTitle className="text-foreground text-base font-['Manrope'] flex items-center gap-2">
              <CreditCard className={`w-4 h-4 ${profitColor}`} />
              Payments by Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {payments_by_type.map((pt, i) => (
                <div key={i} className="bg-theme-surface rounded-lg px-4 py-2 border border-theme">
                  <p className="text-foreground font-medium">{pt.name}</p>
                  <p className={`${profitColor} font-semibold`}>{formatCurrency(pt.amount)}</p>
                  <p className="text-muted-foreground text-xs">{pt.count} transactions</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="glass-card border-theme">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-foreground text-base font-['Manrope']">Direct Job Details</CardTitle>
          <Button variant="outline" size="sm" onClick={() => exportToCSV(jobs, "direct_jobs_report")} className={btnOutline} data-testid="export-direct-btn">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-theme hover:bg-transparent">
                  <TableHead className="text-muted-foreground">Job ID</TableHead>
                  <TableHead className="text-muted-foreground">Date</TableHead>
                  <TableHead className="text-muted-foreground">Customer</TableHead>
                  <TableHead className="text-muted-foreground">Address</TableHead>
                  <TableHead className="text-muted-foreground">ZIP</TableHead>
                  <TableHead className="text-muted-foreground">Job Type</TableHead>
                  <TableHead className="text-muted-foreground text-right">Total</TableHead>
                  <TableHead className="text-muted-foreground text-right" title="Sales tax on the job — excluded from revenue and profit">Tax</TableHead>
                  <TableHead className="text-muted-foreground">Payment By</TableHead>
                  <TableHead className="text-muted-foreground text-right">Parts</TableHead>
                  <TableHead className="text-muted-foreground text-right">Net Profit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.length === 0 ? (
                  <TableRow className="border-theme">
                    <TableCell colSpan={11} className="text-center text-muted-foreground py-8">No direct jobs found for selected period</TableCell>
                  </TableRow>
                ) : (
                  jobs.map((job, i) => (
                    <TableRow key={job.job_id || i} className="border-theme hover:bg-theme-surface">
                      <TableCell>
                        <Link to={`/dashboard/jobs?job=${job.job_id}`} className={`${linkColor} flex items-center gap-1 font-mono text-xs`} data-testid={`direct-job-link-${job.job_id}`}>
                          {job.job_id?.slice(-8) || "—"}<ExternalLink className="w-3 h-3" />
                        </Link>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">{formatDate(job.completed_at)}</TableCell>
                      <TableCell className="text-foreground font-medium">{job.customer_name}</TableCell>
                      <TableCell className="text-muted-foreground text-sm max-w-[150px] truncate">{job.address || "N/A"}</TableCell>
                      <TableCell className="text-muted-foreground">{job.zip || "—"}</TableCell>
                      <TableCell className="text-foreground">{job.job_type}</TableCell>
                      <TableCell className="text-green-600 text-right font-medium">{formatCurrency(job.total)}</TableCell>
                      <TableCell className="text-teal-600 text-right text-sm" data-testid={`direct-tax-${job.job_id}`}>{(job.tax || 0) > 0 ? formatCurrency(job.tax) : "—"}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{job.payment_by}</TableCell>
                      <TableCell className="text-orange-500 text-right">{formatCurrency(job.parts)}</TableCell>
                      <TableCell className={`${profitColor} text-right font-semibold`}>{formatCurrency(job.net_profit)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DirectJobsReportTab;
